import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity, Users, BookOpen, Database, FileText, FileSignature, Truck, FileCheck,
  Settings, Search, ScrollText, Menu, X, Shield, Home, BarChart3, Package, Tag,
  ChevronDown, Building2,
} from "lucide-react";
import { exitAzienda } from "@/lib/azienda";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { CommandPalette } from "@/components/CommandPalette";
import { CerQuotaBanner } from "@/components/CerQuotaBanner";
import { BaseVoucherBanner } from "@/components/BaseVoucherBanner";
import { CreateMenu } from "@/components/CreateMenu";
import { OperatoreContextBar } from "@/components/OperatoreContextBar";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { AziendaBar } from "@/components/AziendaBar";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api/client";
import { Button } from "@/components/ui/button";
import { authDisabled } from "@/lib/authConfig";
import { useAuth } from "@/lib/AuthProvider";
import { cn } from "@/lib/utils";

const NAV_OPERATIVO = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/registri", label: "Registro", icon: Database },
  { to: "/giacenze", label: "Giacenze", icon: Package },
  { to: "/formulari", label: "Formulari", icon: FileText },
  { to: "/cer-attivi", label: "CER attivi", icon: Tag },
  { to: "/contacts", label: "Operatori", icon: Truck },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings },
];

const NAV_AVANZATO = [
  { to: "/anagrafiche", label: "Anagrafiche", icon: Users },
  { to: "/codifiche", label: "Codifiche", icon: BookOpen },
  { to: "/vidimazione", label: "Vidimazione", icon: FileSignature },
  { to: "/drafts", label: "Bozze", icon: ScrollText },
  { to: "/jobs", label: "Job RENTRI", icon: FileCheck },
  { to: "/audit", label: "Audit log", icon: Activity },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/autista", label: "Mod. autista", icon: Truck },
];

export function AppLayout() {
  const [palOpen, setPalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avanzatoOpen, setAvanzatoOpen] = useState(() => {
    try {
      const raw = localStorage.getItem("rentri.userPreferences");
      if (raw) {
        const p = JSON.parse(raw) as { avanzato_aperto?: boolean };
        return p.avanzato_aperto === true;
      }
    } catch {
      /* */
    }
    return false;
  });
  const [dark, setDark] = useState(() => localStorage.getItem("rentri.theme") === "dark");
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("rentri.theme", dark ? "dark" : "light");
  }, [dark]);

  const sidebar = (onNav?: () => void) => (
    <>
      <Brand />
      <OperatoreContextBar />
      <CreateMenu />
      <nav className="flex-1 overflow-y-auto p-3 scrollbar-thin space-y-0.5">
        <GestoreNavLink onClick={onNav} />
        {NAV_OPERATIVO.map((n) => (
          <NavItem key={n.to} {...n} onClick={onNav} />
        ))}
        <button
          type="button"
          onClick={() => setAvanzatoOpen((v) => !v)}
          className="mt-3 flex w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-accent"
        >
          Strumenti avanzati
          <ChevronDown className={cn("h-4 w-4 transition", avanzatoOpen && "rotate-180")} />
        </button>
        {avanzatoOpen &&
          NAV_AVANZATO.map((n) => <NavItem key={n.to} {...n} onClick={onNav} />)}
        <AdminNavLink onClick={onNav} />
      </nav>
      <div className="border-t p-3 text-xs text-muted-foreground">
        <UserMenu />
      </div>
    </>
  );

  return (
    <div className="flex h-full bg-muted/30">
      <aside className="hidden w-64 flex-col border-r bg-background lg:flex">
        {sidebar()}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <aside
            className="absolute left-0 top-0 flex h-full w-72 flex-col bg-background shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <Brand compact />
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            {sidebar(() => setMobileOpen(false))}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <AziendaBar />
        <CerQuotaBanner />
        <BaseVoucherBanner />
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/80 px-3 backdrop-blur lg:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="ml-1 w-72 justify-between text-muted-foreground"
            onClick={() => setPalOpen(true)}
          >
            <span className="flex items-center gap-2">
              <Search className="h-4 w-4" /> Cerca…
            </span>
            <kbd className="hidden rounded border bg-muted px-1.5 text-[10px] sm:inline-block">⌘K</kbd>
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDark((d) => !d)}
              title={dark ? "Tema chiaro" : "Tema scuro"}
            >
              {dark ? "☀️" : "🌙"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/impostazioni")}
              className="hidden sm:inline-flex"
            >
              <Shield className="mr-2 h-4 w-4" /> P12
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={palOpen} onOpenChange={setPalOpen} />
      <Toaster richColors position="top-right" />
    </div>
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cn("flex items-center gap-3 border-b px-4", compact ? "py-2" : "py-4")}>
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary font-bold text-primary-foreground">
        R
      </div>
      <div className="flex flex-col leading-tight">
        <span className="font-semibold">RENTRI Suite</span>
        <span className="text-[11px] text-muted-foreground">Quality &amp; Safety</span>
      </div>
    </div>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  end,
  onClick,
}: {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        )
      }
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </NavLink>
  );
}

function AdminNavLink({ onClick }: { onClick?: () => void }) {
  const auth = useAuth();
  if (!auth.isAdmin) return null;
  return (
    <NavLink
      to="/admin"
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "mt-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        )
      }
    >
      <Shield className="h-4 w-4" />
      <span>Amministrazione</span>
    </NavLink>
  );
}

function GestoreNavLink({ onClick }: { onClick?: () => void }) {
  const statusQ = useQuery({
    queryKey: ["billing-status"],
    queryFn: endpoints.billing.status,
    staleTime: 10_000,
  });
  const s = statusQ.data;
  if (!s?.is_gestore) return null;
  // Dentro un'azienda il link deve prima uscire dall'azienda (ricarica), fuori è un NavLink normale.
  if (s.managed_by) {
    return (
      <button
        type="button"
        onClick={() => exitAzienda()}
        className="mb-2 flex w-full items-center gap-3 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-sm font-medium hover:bg-sky-500/20"
      >
        <Building2 className="h-4 w-4" /> Le mie aziende
      </button>
    );
  }
  return (
    <div className="mb-2">
      <NavItem to="/gestore" label="Le mie aziende" icon={Building2} onClick={onClick} />
    </div>
  );
}

function UserMenu() {
  const auth = useAuth();
  const label = auth.email || "Utente";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-md p-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary font-bold text-secondary-foreground">
          {label.slice(0, 1).toUpperCase()}
        </div>
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate font-medium text-foreground">{label}</span>
          {auth.impersonating && (
            <span className="text-[10px] text-amber-700 dark:text-amber-400">impersonificazione</span>
          )}
          {authDisabled && (
            <button
              type="button"
              className="text-left text-[10px] text-primary underline"
              onClick={() => void auth.login()}
            >
              cambia utente dev
            </button>
          )}
        </div>
      </div>
      <Button variant="outline" size="sm" className="w-full" onClick={() => void auth.logout()}>
        Esci
      </Button>
    </div>
  );
}
